import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Plus,
  Trash2,
  Edit2,
  Users,
  TrendingDown,
  TrendingUp,
  Package,
  Eye,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const COLORS = [
  { name: "Blue", value: "#3B82F6" },
  { name: "Green", value: "#10B981" },
  { name: "Purple", value: "#8B5CF6" },
  { name: "Orange", value: "#F59E0B" },
  { name: "Red", value: "#EF4444" },
  { name: "Pink", value: "#EC4899" },
  { name: "Teal", value: "#14B8A6" },
];

export default function WatchGroups() {
  const { isAdmin } = useOutletContext() || {};
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editGroup, setEditGroup] = useState(null);
  const [viewGroup, setViewGroup] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [newGroup, setNewGroup] = useState({
    name: "",
    description: "",
    color: "#3B82F6",
    product_asins: [],
  });
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchGroups();
    fetchProducts();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("pricelens_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchGroups = async () => {
    try {
      const { data } = await axios.get(`${API}/watch-groups`, {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      setGroups(data.groups || []);
    } catch (error) {
      console.error("Failed to fetch groups:", error);
      toast.error("Failed to load watch groups");
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const { data } = await axios.get(`${API}/products`, {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      setAllProducts(data || []);
    } catch (error) {
      console.error("Failed to fetch products:", error);
    }
  };

  const createGroup = async () => {
    if (!newGroup.name.trim()) {
      toast.error("Group name is required");
      return;
    }

    try {
      await axios.post(`${API}/watch-groups`, newGroup, {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      toast.success("Watch group created");
      setCreateOpen(false);
      setNewGroup({ name: "", description: "", color: "#3B82F6", product_asins: [] });
      fetchGroups();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create group");
    }
  };

  const updateGroup = async () => {
    if (!editGroup) return;

    try {
      await axios.put(`${API}/watch-groups/${editGroup.id}`, {
        name: editGroup.name,
        description: editGroup.description,
        color: editGroup.color,
        product_asins: editGroup.product_asins,
      }, {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      toast.success("Watch group updated");
      setEditGroup(null);
      fetchGroups();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update group");
    }
  };

  const deleteGroup = async (groupId) => {
    try {
      await axios.delete(`${API}/watch-groups/${groupId}`, {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      toast.success("Watch group deleted");
      fetchGroups();
    } catch (error) {
      toast.error("Failed to delete group");
    }
  };

  const toggleProductInGroup = (asin, isEdit = false) => {
    const target = isEdit ? editGroup : newGroup;
    const setter = isEdit ? setEditGroup : setNewGroup;
    
    const current = target.product_asins || [];
    const updated = current.includes(asin)
      ? current.filter((a) => a !== asin)
      : [...current, asin];
    
    setter({ ...target, product_asins: updated });
  };

  const filteredProducts = allProducts.filter(
    (p) =>
      p.asin?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.title?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Watch Groups</h1>
          <p className="text-gray-500 text-sm mt-1">
            Create custom groups to track specific competitor bundles
          </p>
        </div>

        {isAdmin && (
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="create-group-btn"
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Group
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create Watch Group</DialogTitle>
              </DialogHeader>

              <div className="space-y-4 py-4">
                <div>
                  <label className="text-sm font-medium">Group Name *</label>
                  <Input
                    value={newGroup.name}
                    onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
                    placeholder="e.g., Premium Mixer Grinders"
                    className="mt-1"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium">Description</label>
                  <Input
                    value={newGroup.description}
                    onChange={(e) => setNewGroup({ ...newGroup, description: e.target.value })}
                    placeholder="Track top competitors in this segment"
                    className="mt-1"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium">Color</label>
                  <div className="flex gap-2 mt-2">
                    {COLORS.map((c) => (
                      <button
                        key={c.value}
                        onClick={() => setNewGroup({ ...newGroup, color: c.value })}
                        className={cn(
                          "w-8 h-8 rounded-full border-2 transition-all",
                          newGroup.color === c.value ? "border-gray-800 scale-110" : "border-transparent"
                        )}
                        style={{ backgroundColor: c.value }}
                        title={c.name}
                      />
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium">
                    Select Products ({newGroup.product_asins.length} selected)
                  </label>
                  <Input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by ASIN or name..."
                    className="mt-2"
                  />
                  <div className="mt-2 max-h-48 overflow-y-auto border rounded-lg">
                    {filteredProducts.slice(0, 50).map((p) => (
                      <div
                        key={p.asin}
                        onClick={() => toggleProductInGroup(p.asin)}
                        className={cn(
                          "p-2 cursor-pointer hover:bg-gray-50 flex items-center justify-between border-b last:border-b-0",
                          newGroup.product_asins.includes(p.asin) && "bg-blue-50"
                        )}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">
                            {p.name || p.title}
                          </p>
                          <p className="text-xs text-gray-500">
                            {p.asin} • {p.category} • {p.is_competition ? "Competition" : "Own"}
                          </p>
                        </div>
                        <input
                          type="checkbox"
                          checked={newGroup.product_asins.includes(p.asin)}
                          readOnly
                          className="ml-2"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setCreateOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={createGroup} className="bg-blue-600 hover:bg-blue-700">
                  Create Group
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Groups Grid */}
      {groups.length === 0 ? (
        <div className="text-center py-16 bg-gray-50 rounded-xl">
          <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-700">No Watch Groups</h3>
          <p className="text-gray-500 mt-1">
            Create groups to track specific competitor bundles together
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {groups.map((group) => (
            <div
              key={group.id}
              className="bg-white rounded-xl border shadow-sm overflow-hidden"
            >
              {/* Group Header */}
              <div
                className="p-4 text-white"
                style={{ backgroundColor: group.color || "#3B82F6" }}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-lg">{group.name}</h3>
                    {group.description && (
                      <p className="text-sm opacity-90 mt-1">{group.description}</p>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-white hover:bg-white/20"
                      onClick={() => setViewGroup(group)}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    {isAdmin && (
                      <>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-white hover:bg-white/20"
                          onClick={() => setEditGroup(group)}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-white hover:bg-white/20"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Group</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete "{group.name}"?
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => deleteGroup(group.id)}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Group Stats */}
              <div className="p-4">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <Package className="h-5 w-5 text-gray-400 mx-auto mb-1" />
                    <p className="text-lg font-semibold">{group.product_count || 0}</p>
                    <p className="text-xs text-gray-500">Products</p>
                  </div>
                  <div>
                    <TrendingDown className="h-5 w-5 text-gray-400 mx-auto mb-1" />
                    <p className="text-lg font-semibold">#{group.avg_bsr || "—"}</p>
                    <p className="text-xs text-gray-500">Avg BSR</p>
                  </div>
                  <div>
                    <TrendingUp className="h-5 w-5 text-gray-400 mx-auto mb-1" />
                    <p className="text-lg font-semibold">{group.decimal_count || 0}</p>
                    <p className="text-xs text-gray-500">Markdowns</p>
                  </div>
                </div>

                {group.avg_price > 0 && (
                  <div className="mt-4 pt-4 border-t text-center">
                    <p className="text-sm text-gray-500">Avg Price</p>
                    <p className="text-xl font-bold text-gray-800">
                      ₹{group.avg_price?.toLocaleString()}
                    </p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* View Group Dialog */}
      <Dialog open={!!viewGroup} onOpenChange={() => setViewGroup(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div
                className="w-4 h-4 rounded"
                style={{ backgroundColor: viewGroup?.color }}
              />
              {viewGroup?.name}
            </DialogTitle>
          </DialogHeader>

          <div className="mt-4">
            <h4 className="font-medium mb-2">Products in this group:</h4>
            <div className="space-y-2">
              {viewGroup?.products?.map((p) => (
                <div
                  key={p.asin}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div>
                    <p className="font-medium">{p.name || p.title}</p>
                    <p className="text-sm text-gray-500">
                      {p.asin} • {p.category}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold">
                      ₹{p.current_price?.toLocaleString() || "—"}
                    </p>
                    <p className="text-sm text-gray-500">BSR #{p.current_bsr || "—"}</p>
                  </div>
                </div>
              ))}
              {(!viewGroup?.products || viewGroup.products.length === 0) && (
                <p className="text-gray-500 text-center py-4">No products in this group</p>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Group Dialog */}
      <Dialog open={!!editGroup} onOpenChange={() => setEditGroup(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Watch Group</DialogTitle>
          </DialogHeader>

          {editGroup && (
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium">Group Name</label>
                <Input
                  value={editGroup.name}
                  onChange={(e) => setEditGroup({ ...editGroup, name: e.target.value })}
                  className="mt-1"
                />
              </div>

              <div>
                <label className="text-sm font-medium">Description</label>
                <Input
                  value={editGroup.description || ""}
                  onChange={(e) => setEditGroup({ ...editGroup, description: e.target.value })}
                  className="mt-1"
                />
              </div>

              <div>
                <label className="text-sm font-medium">Color</label>
                <div className="flex gap-2 mt-2">
                  {COLORS.map((c) => (
                    <button
                      key={c.value}
                      onClick={() => setEditGroup({ ...editGroup, color: c.value })}
                      className={cn(
                        "w-8 h-8 rounded-full border-2 transition-all",
                        editGroup.color === c.value ? "border-gray-800 scale-110" : "border-transparent"
                      )}
                      style={{ backgroundColor: c.value }}
                    />
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium">
                  Products ({editGroup.product_asins?.length || 0} selected)
                </label>
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search..."
                  className="mt-2"
                />
                <div className="mt-2 max-h-48 overflow-y-auto border rounded-lg">
                  {filteredProducts.slice(0, 50).map((p) => (
                    <div
                      key={p.asin}
                      onClick={() => toggleProductInGroup(p.asin, true)}
                      className={cn(
                        "p-2 cursor-pointer hover:bg-gray-50 flex items-center justify-between border-b last:border-b-0",
                        editGroup.product_asins?.includes(p.asin) && "bg-blue-50"
                      )}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{p.name || p.title}</p>
                        <p className="text-xs text-gray-500">{p.asin}</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={editGroup.product_asins?.includes(p.asin)}
                        readOnly
                        className="ml-2"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditGroup(null)}>
              Cancel
            </Button>
            <Button onClick={updateGroup} className="bg-blue-600 hover:bg-blue-700">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
